import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { usePlan } from './PlanContext';
import { useTwin } from './TwinContext';

// ── Types ──────────────────────────────────────────────────────

export type MachineStatus = 'active' | 'idle' | 'maintenance' | 'offline';

export interface Sensor {
  sensorId:         string;
  machineId:        string;
  name:             string;
  type:             'temperature' | 'vibration' | 'rpm' | 'pressure' | 'humidity' | 'current';
  unit:             string;
  minThreshold:     number;
  maxThreshold:     number;
  samplingInterval: number; // seconds
  currentValue?:    number;
}

export interface Machine {
  machineId:   string;
  workspaceId: string;
  factoryId?:  string;
  name:        string;
  type:        string;
  location:    string;
  status:      MachineStatus;
  sensors:     Sensor[];
  createdAt:   string;
}

// ── Mock seed data ─────────────────────────────────────────────

const SEED_MACHINES: Machine[] = [
  {
    machineId: 'machine_001', workspaceId: 'demo', factoryId: 'factory_cookie_main',
    name: 'Mixer Alpha', type: 'Industrial Mixer', location: 'Building A — Line 1',
    status: 'active', createdAt: new Date().toISOString(),
    sensors: [
      { sensorId: 's_001', machineId: 'machine_001', name: 'Motor Temp',  type: 'temperature', unit: '°C',  minThreshold: 0,  maxThreshold: 85,  samplingInterval: 5,  currentValue: 72 },
      { sensorId: 's_002', machineId: 'machine_001', name: 'Shaft RPM',   type: 'rpm',         unit: 'RPM', minThreshold: 0,  maxThreshold: 1200, samplingInterval: 1, currentValue: 840 },
      { sensorId: 's_003', machineId: 'machine_001', name: 'Vibration',   type: 'vibration',   unit: 'g',   minThreshold: 0,  maxThreshold: 2.5, samplingInterval: 2,  currentValue: 0.8 },
    ],
  },
  {
    machineId: 'machine_002', workspaceId: 'demo', factoryId: 'factory_cookie_main',
    name: 'Conveyor Belt B2', type: 'Conveyor', location: 'Building A — Line 2',
    status: 'idle', createdAt: new Date().toISOString(),
    sensors: [
      { sensorId: 's_004', machineId: 'machine_002', name: 'Belt Speed',  type: 'rpm',         unit: 'RPM', minThreshold: 0,  maxThreshold: 500, samplingInterval: 2,  currentValue: 0 },
      { sensorId: 's_005', machineId: 'machine_002', name: 'Motor Temp',  type: 'temperature', unit: '°C',  minThreshold: 0,  maxThreshold: 70,  samplingInterval: 5,  currentValue: 28 },
    ],
  },
  {
    machineId: 'machine_003', workspaceId: 'demo', factoryId: 'factory_cookie_pack',
    name: 'Packaging Unit P1', type: 'Packaging Machine', location: 'Building B — Pack Line',
    status: 'maintenance', createdAt: new Date().toISOString(),
    sensors: [
      { sensorId: 's_006', machineId: 'machine_003', name: 'Air Pressure', type: 'pressure',  unit: 'bar', minThreshold: 4,  maxThreshold: 8,   samplingInterval: 3,  currentValue: 5.2 },
    ],
  },
];

// ── Sensor defaults by type ────────────────────────────────────

export const SENSOR_DEFAULTS: Record<Sensor['type'], Omit<Sensor, 'sensorId' | 'machineId' | 'name'>> = {
  temperature: { type: 'temperature', unit: '°C',  minThreshold: 0,  maxThreshold: 100, samplingInterval: 5  },
  vibration:   { type: 'vibration',   unit: 'g',   minThreshold: 0,  maxThreshold: 5,   samplingInterval: 2  },
  rpm:         { type: 'rpm',         unit: 'RPM', minThreshold: 0,  maxThreshold: 3000, samplingInterval: 1 },
  pressure:    { type: 'pressure',    unit: 'bar', minThreshold: 0,  maxThreshold: 10,  samplingInterval: 3  },
  humidity:    { type: 'humidity',    unit: '%',   minThreshold: 20, maxThreshold: 80,  samplingInterval: 10 },
  current:     { type: 'current',     unit: 'A',   minThreshold: 0,  maxThreshold: 50,  samplingInterval: 2  },
};

// ── Context ────────────────────────────────────────────────────

interface MachineCtx {
  machines:      Machine[];
  loading:       boolean;
  addMachine:    (m: Omit<Machine, 'machineId' | 'sensors' | 'createdAt'>) => Machine | null;
  updateMachine: (machineId: string, patch: Partial<Machine>) => void;
  deleteMachine: (machineId: string) => void;
  addSensor:     (machineId: string, s: Omit<Sensor, 'sensorId' | 'machineId'>) => boolean;
  deleteSensor:  (machineId: string, sensorId: string) => void;
}

const MachineContext = createContext<MachineCtx>({} as MachineCtx);

export const MachineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [machines, setMachines] = useState<Machine[]>(SEED_MACHINES);
  const [loading]               = useState(false);
  const { checkMachines, checkSensors } = usePlan();
  const { getActiveTwin } = useTwin();

  // Sync machines from active twin
  useEffect(() => {
    const activeTwin = getActiveTwin();
    if (activeTwin && activeTwin.machines && activeTwin.machines.length > 0) {
      // Convert Twin machines to Machine format
      const twinMachines: Machine[] = activeTwin.machines.map(m => {
        // Map Twin status ('running'|'idle'|'fault') to MachineStatus ('active'|'idle'|'maintenance'|'offline')
        let machineStatus: MachineStatus = 'idle';
        if (m.status === 'running') machineStatus = 'active';
        else if (m.status === 'fault') machineStatus = 'offline';
        else if (m.status === 'idle') machineStatus = 'idle';
        
        return {
          machineId: m.machineId,
          workspaceId: activeTwin.id,
          factoryId: activeTwin.id,
          name: m.name,
          type: m.name,
          location: `${activeTwin.company} - ${m.name}`,
          status: machineStatus,
          sensors: m.sensors.map(s => ({
            sensorId: s.sensorId,
            machineId: m.machineId,
            name: s.name,
            type: (s.type as any) || 'temperature',
            unit: s.unit || '°C',
            minThreshold: s.min || 0,
            maxThreshold: s.max || 100,
            samplingInterval: 5,
            currentValue: s.value,
          })),
          createdAt: new Date().toISOString(),
        };
      });
      setMachines(twinMachines);
    }
  }, [getActiveTwin]);

  const addMachine = useCallback((m: Omit<Machine, 'machineId' | 'sensors' | 'createdAt'>): Machine | null => {
    if (checkMachines().isBlocked) return null;
    const machine: Machine = {
      ...m,
      machineId: `machine_${Date.now()}`,
      sensors:   [],
      createdAt: new Date().toISOString(),
    };
    setMachines(prev => [...prev, machine]);
    return machine;
  }, [checkMachines]);

  const updateMachine = useCallback((machineId: string, patch: Partial<Machine>) => {
    setMachines(prev => prev.map(m => m.machineId === machineId ? { ...m, ...patch } : m));
  }, []);

  const deleteMachine = useCallback((machineId: string) => {
    setMachines(prev => prev.filter(m => m.machineId !== machineId));
  }, []);

  const addSensor = useCallback((machineId: string, s: Omit<Sensor, 'sensorId' | 'machineId'>): boolean => {
    const machine = machines.find(m => m.machineId === machineId);
    if (!machine) return false;
    if (checkSensors(machineId, machine.sensors.length).isBlocked) return false;
    const sensor: Sensor = { ...s, sensorId: `s_${Date.now()}`, machineId };
    setMachines(prev => prev.map(m =>
      m.machineId === machineId ? { ...m, sensors: [...m.sensors, sensor] } : m
    ));
    return true;
  }, [machines, checkSensors]);

  const deleteSensor = useCallback((machineId: string, sensorId: string) => {
    setMachines(prev => prev.map(m =>
      m.machineId === machineId
        ? { ...m, sensors: m.sensors.filter(s => s.sensorId !== sensorId) }
        : m
    ));
  }, []);

  return (
    <MachineContext.Provider value={{ machines, loading, addMachine, updateMachine, deleteMachine, addSensor, deleteSensor }}>
      {children}
    </MachineContext.Provider>
  );
};

export const useMachines = () => useContext(MachineContext);
